/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { WorkflowDetailDto } from '@ink-ai/insight-service-sdk';
import { formatDate } from '@ink-ai/portal/common/utils';
import { PaginatedTable } from '../../../common/components/paginated-table/PaginatedTable';
import { EllipsisCell } from '../../../common/components/paginated-table/EllipsisCell';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@ink-ai/portal/reducers';
import { listWorkflows } from '@ink-ai/portal/reducers/workflow';
import { Button } from '@mui/material';
import PlayArrowOutlinedIcon from '@mui/icons-material/PlayArrowOutlined';

interface WorkflowTableProps {
  selectedWorkflows: string[];
  searchTerm: string;
  page: number;
  onPageChange: (page: number) => void;
  onSelectedWorkflowsChange: (selectedWorkflows: string[]) => void;
}

export const WorkflowTable: React.FC<WorkflowTableProps> = ({
  selectedWorkflows,
  onSelectedWorkflowsChange,
  searchTerm,
  page,
  onPageChange,
}) => {
  const dispatch = useDispatch<any>();
  const workflow = useSelector((state: RootState) => state.workflow);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    dispatch(listWorkflows());
  }, [dispatch]);

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const currentPageWorkflows = filteredWorkflows.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage,
      );
      const newSelecteds = currentPageWorkflows.map((w) => w.workflowId);
      onSelectedWorkflowsChange(newSelecteds);
    } else {
      onSelectedWorkflowsChange([]);
    }
  };

  const handleClick = (uuid: string) => {
    const selectedIndex = selectedWorkflows.indexOf(uuid);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedWorkflows, uuid);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedWorkflows.slice(1));
    } else if (selectedIndex === selectedWorkflows.length - 1) {
      newSelected = newSelected.concat(selectedWorkflows.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedWorkflows.slice(0, selectedIndex),
        selectedWorkflows.slice(selectedIndex + 1),
      );
    }

    onSelectedWorkflowsChange(newSelected);
  };

  const handlePageChange = (
    _: React.ChangeEvent<HTMLInputElement>,
    newPage: number,
  ) => {
    onPageChange(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    onPageChange(0);
  };

  const filteredWorkflows = useMemo(
    () =>
      (workflow.workflows || []).filter((w) =>
        w.name.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [workflow.workflows, searchTerm],
  );

  const { t } = useTranslation();
  const navigate = useNavigate();

  const columns = [
    {
      id: 'name',
      label: t('table.name'),
      render: (workflow: WorkflowDetailDto) => (
        <EllipsisCell content={workflow.name} tooltip={workflow.name} maxLines={3} />
      ),
      width: 150,
    },
    {
      id: 'type',
      label: t('table.type'),
      render: (workflow: WorkflowDetailDto) => {
        const type = workflow.type || 'dify';
        return t(`workflows.type.${type}`);
      },
      width: 100,
    },
    {
      id: 'apiEndpoint',
      label: t('table.endpoint'),
      render: (workflow: WorkflowDetailDto) => (
        <EllipsisCell
          content={workflow.apiEndpoint}
          tooltip={workflow.apiEndpoint}
        />
      ),
      width: 150,
    },
    {
      id: 'desc',
      label: t('table.description'),
      render: (workflow: WorkflowDetailDto) => (
        <EllipsisCell content={workflow.description || '-'} maxLines={3} />
      ),
      width: 150,
    },
    {
      id: 'updatedAt',
      label: t('table.updatedAt'),
      render: (workflow: WorkflowDetailDto) =>
        workflow.updatedAt
          ? formatDate(new Date(workflow.updatedAt).getTime())
          : '-',
      width: 150,
    },
    {
      id: 'actions',
      label: '',
      render: (workflow: WorkflowDetailDto) => (
        <Button
          onClick={() => navigate(`/workflows/${workflow.workflowId}/invoke`)}
          startIcon={<PlayArrowOutlinedIcon />}
          variant="contained"
        >
          {t('table.startWorkflow')}
        </Button>
      ),
      width: 150,
    },
  ];

  return (
    <PaginatedTable
      dummyPagination
      columns={columns}
      data={filteredWorkflows}
      selectedItems={selectedWorkflows}
      page={page}
      rowsPerPage={rowsPerPage}
      loading={workflow.loading}
      getItemId={(workflow) => workflow.workflowId}
      onSelectAllClick={handleSelectAllClick}
      onItemClick={handleClick}
      onPageChange={handlePageChange}
      onRowsPerPageChange={handleRowsPerPageChange}
    />
  );
};
